/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountsApiEnum } from '../enums/api';
import { ApiService, AuthStoreService, ConnectionDetailsService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { ExternalUtilsService } from '../../../../pages/shared/external-utils-service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subscriber, throwError as observableThrowError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

@Injectable()
export class AccountsApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService,
        private connectionDetailsService: ConnectionDetailsService,
        private externalUtilsService: ExternalUtilsService,
        private http: HttpClient
    ) {
        this.connectionDetailsService.getUrl();
    }

    /**
     * Get specific account data by ID
     * @param id
     * @param options
     */
    public get(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(
                AccountsApiEnum.GET_ACCOUNTS(id),
                moduleAuth
            ).subscribe({
                next: (data: any): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Delete an account by ID
     * @param id
     * @param options
     */
    public delete(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.delete(
                AccountsApiEnum.DELETE_ACCOUNTS(id),
                moduleAuth
            ).subscribe(
                (data: any) => {
                    observer.next(data);
                    observer.complete();
                },
                (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Edit an account by ID
     * @param id
     * @param userObject
     * @param options
     */
    public update(id: string, userObject: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.patch(
                AccountsApiEnum.PATCH_ACCOUNTS_UPDATE(id),
                userObject,
                moduleAuth
            ).subscribe(
                (data: any) => {
                    observer.next(data);
                    observer.complete();
                },
                (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Create a user
     * @param userObject
     */
    public create(userObject: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiUrl = this.connectionDetailsService.getUrl();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.post(apiUrl + AccountsApiEnum.POST_ACCOUNTS_ADD, userObject)
                .pipe(
                    timeout(10000),
                    catchError(
                        err => {
                            return observableThrowError(err);
                        }
                    )
                )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                observer.complete();
                                break;
                            default:
                                break;
                        }

                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Activate a user
     * @param accountID
     * @param activationCode
     * @param userObject
     */
    public activate(accountID: string, activationCode: string, userObject: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiUrl = this.connectionDetailsService.getUrl();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.patch(
                apiUrl + AccountsApiEnum.PATCH_ACCOUNTS_ACTIVATE(accountID, activationCode),
                userObject
            ).pipe(
                timeout(10000),
                catchError(
                    err => {
                        return observableThrowError(err);
                    }
                )
            )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                observer.complete();
                                break;
                            default:
                                break;
                        }

                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get temporary activation keys for an invited user.
     * @param id
     * @param token
     */
    public getActivationKeys(id: string, token: string): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiUrl = this.connectionDetailsService.getUrl();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.get(apiUrl + AccountsApiEnum.GET_ACCOUNTS_ACTIVATE_INVITE(id, token))
                .pipe(
                    timeout(10000),
                    catchError(
                        err => {
                            return observableThrowError(err);
                        }
                    )
                )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                observer.complete();
                                break;
                            default:
                                break;
                        }

                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Recovery password
     * @param data
     */
    public recoverPassword(data: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiUrl = this.connectionDetailsService.getUrl();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.post(apiUrl + AccountsApiEnum.POST_ACCOUNTS_RECOVER, data)
                .pipe(
                    timeout(10000),
                    catchError(
                        err => {
                            return observableThrowError(err);
                        }
                    )
                )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                observer.complete();
                                break;
                            default:
                                break;
                        }

                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(this.externalUtilsService.formatErrorMessage(error));
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete an account by ID
     * @param id
     * @param options
     */
    public deleteSSO(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.delete(
                AccountsApiEnum.DELETE_ACCOUNTS_SSO(id),
                moduleAuth
            ).subscribe(
                (data: any) => {
                    observer.next(data);
                    observer.complete();
                },
                (error: ErrorResponseModel) => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    /**
     * Activate a marketing user.
     * @param hubspotId
     * @param userObject
     */
    public activateMarketing(hubspotId: string, userObject: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiUrl = this.connectionDetailsService.getUrl();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.patch(
                apiUrl + AccountsApiEnum.PATCH_ACCOUNTS_MARKETING_ACTIVATE(hubspotId),
                userObject
            ).pipe(
                timeout(10000),
                catchError(
                    err => {
                        return observableThrowError(err);
                    }
                )
            )
                .subscribe({
                    next: (response: any): void => {
                        // Handle our response status code
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                observer.complete();
                                break;
                            default:
                                break;
                        }

                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(this.externalUtilsService.formatErrorMessage(error, userObject));
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Activate a marketing user.
     * @param hubspotId
     */
    public getHubspotAccount(hubspotId: string): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiUrl = this.connectionDetailsService.getUrl();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');

            this.http.get(
                apiUrl + AccountsApiEnum.GET_ACCOUNTS_MARKETING_ACTIVATE(hubspotId)
            ).pipe(
                timeout(10000),
                catchError(
                    err => {
                        return observableThrowError(err);
                    }
                )
            )
                .subscribe({
                    next: (response: any) => {
                        switch (response.status) {
                            case 'error':
                            case 'fail':
                                observer.error(response.message);
                                observer.complete();
                                break;
                            default:
                                break;
                        }

                        observer.next(response.data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }
}
